import { useLayoutEffect, useRef } from 'react';

type TransitionTimingFunction =
	| 'ease'
	| 'ease-in'
	| 'ease-out'
	| 'ease-in-out'
	| 'linear'
	| 'cubic-bezier(0.4, 0, 0.2, 1)'
	| 'cubic-bezier(0.4, 0, 1, 1)'
	| 'cubic-bezier(0, 0, 0.2, 1)'
	| 'cubic-bezier(0, 0, 1, 1)'
	| (string & {});

interface SliderProps {
	children: React.ReactNode;
	isOpen: boolean;

	/**
	 * The duration of the transition in milliseconds.
	 * @default 300
	 */
	transitionDuration?: number;

	/**
	 * The timing function for the transition.
	 * @default 'ease-in-out'
	 */
	transitionTimingFunction?: TransitionTimingFunction;

	/**
	 * Whether to animate opacity in addition to height.
	 * @default false
	 */
	animateOpacity?: boolean;
}
function Slider({
	children,
	transitionDuration = 300,
	transitionTimingFunction = 'ease-out',
	animateOpacity = false,
	isOpen,
}: SliderProps) {
	const sliderContentRef = useRef<HTMLDivElement>(null);
	const sliderContainerRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		toggleSlider(sliderContainerRef, sliderContentRef, isOpen, animateOpacity);
	}, [isOpen, animateOpacity]);

	function handleTransitionEnd(e: React.TransitionEvent<HTMLDivElement>) {
		// Handle height transition
		if (isOpen && e.propertyName === 'height' && e.target === sliderContainerRef.current) {
			sliderContainerRef.current.style.height = 'auto';
		}
	}

	const transitionStyle = {
		transitionDuration: `${transitionDuration}ms`,
		transitionProperty: animateOpacity ? 'height, opacity' : 'height',
		transitionTimingFunction,
		...(animateOpacity && {
			opacity: isOpen ? 1 : 0,
		}),
	};

	return (
		<div
			ref={sliderContainerRef}
			className="overflow-hidden"
			style={transitionStyle}
			aria-hidden={!isOpen}
			onTransitionEnd={handleTransitionEnd}
		>
			<div ref={sliderContentRef}>{children}</div>
		</div>
	);
}

function toggleSlider(
	sliderContainerRef: React.RefObject<HTMLDivElement | null>,
	sliderContentRef: React.RefObject<HTMLDivElement | null>,
	isOpen: boolean,
	_animateOpacity: boolean,
) {
	if (!sliderContainerRef.current || !sliderContentRef.current) {
		return;
	}

	const contentHeight = sliderContentRef.current.offsetHeight;
	const containerHeight = sliderContainerRef.current.clientHeight;
	// console.log({ contentHeight, containerHeight });

	if (isOpen) {
		/* height is 0 -> we set it to content height -> transition end event will set it to auto */
		if (containerHeight === contentHeight) {
			// on first render when isOpen is true. set to auto directly (we have no transition end event)
			sliderContainerRef.current.style.height = 'auto';
		} else {
			sliderContainerRef.current.style.height = `${contentHeight}px`;
		}
	} else {
		// Closing: first set to the current height and then animate to 0 height
		sliderContainerRef.current.style.height = `${contentHeight}px`;
		// biome-ignore lint/nursery/noUnusedExpressions: Forcing reflow
		sliderContainerRef.current.offsetHeight;
		// animate to 0 height
		sliderContainerRef.current.style.height = '0';
	}
}

export { Slider };
